import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import UserContext from '../../contexts/UserContext';
import authenticate from '../../utils/Authenticate';

const MenuDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [menu, setMenu] = useState(null);
  const [message, setMessage] = useState('');
  const { user, setUser } = useContext(UserContext);
  
  // Modal and editing states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editedDishes, setEditedDishes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Add new dish states
  const [showAddDish, setShowAddDish] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedDish, setSelectedDish] = useState(null);

  useEffect(() => {
    async function startup() {
      if (!user) {
        await authenticate(setUser, navigate);
      }
    }
    startup();
  }, [user]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URI}/api/menu/get?id=${id}`, {
      withCredentials: true
    })
      .then(res => {
        setMenu(res.data.data);
        setEditedDishes(res.data.data.dishes || []);
      })
      .catch(err => {
        setMessage("Failed to load menu details");
      });
  }, [id]);

  // Search for dishes to add
  const handleSearchDishes = async (searchValue) => {
    setSearchTerm(searchValue);
    
    // if (searchValue.length < 2) {
    //   setSearchResults([]);
    //   return;
    // }

    try {
      setSearchLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_URI}/api/menu/getAllDishes`, { 
        withCredentials: true 
      });
      
      // Filter dishes based on search term
      const filteredDishes = response.data.data.filter(dish =>
        dish.name.toLowerCase().includes(searchValue.toLowerCase())
      );
      
      setSearchResults(filteredDishes || []);
    } catch (error) {
      console.error("Error searching dishes:", error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Select dish from search results
  const handleSelectDish = (dish) => {
    setSelectedDish(dish);
    setSearchResults([]);
    setSearchTerm(dish.name);
  };

  // Add new dish to the menu
  const handleAddDish = () => {
    if (!selectedDish) {
      setMessage("Please select a dish");
      return;
    }

    // Check if dish already exists
    const exists = editedDishes.some(item => item.dish._id === selectedDish._id);
    if (exists) {
      setMessage("This dish is already in the menu");
      return;
    }

    const newDishItem = {
      dish: {
        _id: selectedDish._id,
        name: selectedDish.name,
        quantity: selectedDish.quantity || 0,
        unit: selectedDish.unit || '',
        ingredients: selectedDish.ingredients || []
      }
    };

    setEditedDishes(prev => [...prev, newDishItem]);
    
    // Reset add dish form
    setShowAddDish(false);
    setSearchTerm('');
    setSearchResults([]);
    setSelectedDish(null);
    setMessage('');
  };

  // Remove dish from menu
  const handleRemoveDish = (dishIndex) => {
    setEditedDishes(prev => prev.filter((_, index) => index !== dishIndex));
  };

  // Cancel add dish
  const handleCancelAddDish = () => {
    setShowAddDish(false);
    setSearchTerm('');
    setSearchResults([]);
    setSelectedDish(null);
  };

  // Save changes to menu
  const handleSaveChanges = async () => {
    setIsLoading(true);
    try {
      const updatedMenu = {
        ...menu,
        dishes: editedDishes
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URI}/api/menu/edit`,
        updatedMenu,
        { withCredentials: true }
      );

      setMenu(response.data.data);
      setIsEditModalOpen(false);
      setMessage("Menu updated successfully!");
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error("Error updating menu:", error);
      setMessage("Failed to update menu");
    } finally {
      setIsLoading(false);
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditedDishes(menu.dishes || []);
    setIsEditModalOpen(false);
    setShowAddDish(false);
    setMessage('');
  };

  if (!menu) return <div className="p-4">Loading...</div>;

  const isArchived = !!menu.isDeleted;

  return (
    <div className="w-full">
      {/* Archived banner */}
      {isArchived && (
        <div className="bg-amber-100 border-b border-amber-300 text-amber-800 text-sm font-medium px-4 py-2 text-center">
          This menu has been archived and is no longer available for new miqaats or requests.
        </div>
      )}

      {/* Header Section */}
      <div className="mb-6 bg-lime-800 p-4 flex items-center">
        <div className="mr-4 cursor-pointer" onClick={() => navigate(-1)}>
          <span className="text-xl text-white">←</span>
        </div>
        <div className="flex-grow text-center">
          <h2 className="text-2xl font-semibold text-white mb-2">{menu.name}</h2>
          {isArchived && (
            <span className="inline-block bg-amber-400 text-amber-900 text-xs font-bold px-2 py-0.5 rounded-full">Archived</span>
          )}
        </div>
        <div className="ml-4">
          {!isArchived && (
            <button
              onClick={() => setIsEditModalOpen(true)}
              className="bg-white text-lime-800 px-4 py-2 rounded hover:bg-gray-100 transition-colors"
            >
              Edit Menu
            </button>
          )}
        </div>
      </div>

      {/* List of Dishes */}
      <h2 className="text-xl font-semibold px-4 text-gray-800 mb-4 text-center">Dishes</h2>

      <div className="max-w-md mx-auto px-4 pb-20">
        {menu.dishes && menu.dishes.length > 0 ? (
          menu.dishes.map((item, index) => {
            const dish = item.dish; // Access the actual dish object

            return (
              <div key={index} className="mb-6 border rounded-lg shadow bg-white overflow-hidden">
                <div className="p-4 border-b">
                  <h3 className="text-lg font-bold text-yellow-700">{dish.name}</h3>
                  <p className="text-sm text-gray-500">
                    Quantity: <strong>{dish.quantity}</strong> {dish.unit}
                  </p>
                </div>

                {/* Ingredients */}
                <h4 className="px-4 py-2 text-sm font-semibold text-lime-700 italic">Ingredients :</h4>
                {dish.ingredients && dish.ingredients.length > 0 ? (
                  <ul className="divide-y">
                    {dish.ingredients.map((ing, idx) => (
                      <li key={idx} className="p-3 px-4">
                        <div className="flex justify-between">
                          <span className="font-semibold">{ing.name?.name || "Unknown Ingredient"}</span>
                          <span>{ing.quantity} {ing.name?.unit || ""}</span>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="p-3 px-4 text-gray-400 italic">No ingredients listed.</p>
                )}
              </div>
            );
          })
        ) : (
          <div className="bg-red-100 text-red-600 p-4 rounded">
            No dishes found in this menu.
          </div>
        )}

        {message && (
          <div className={`mt-4 p-4 rounded ${
            message.includes('successfully') 
              ? 'bg-green-100 text-green-600' 
              : 'bg-red-100 text-red-500'
          }`}>
            {message}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-lime-800 text-white p-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold">Edit Menu: {menu.name}</h3>
              <button
                onClick={handleCancelEdit}
                className="text-white hover:text-gray-200 text-2xl"
              >
                ×
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Add New Dish Section */}
              <div className="mb-6">
                {!showAddDish ? (
                  <button
                    onClick={() => setShowAddDish(true)}
                    className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
                  >
                    + Add New Dish
                  </button>
                ) : (
                  <div className="p-4 border border-green-300 rounded-lg bg-green-50">
                    <h4 className="text-md font-semibold text-green-700 mb-3">
                      Add New Dish
                    </h4>

                    <div className="flex items-end gap-4">
                      {/* Dish Name with Search */}
                      <div className="relative flex-1">
                        <label className="block text-gray-700 text-sm font-bold mb-2">
                          Dish Name *
                        </label>
                        <input
                          type="text"
                          value={searchTerm}
                          onChange={(e) => handleSearchDishes(e.target.value)}
                          placeholder="Type to search dishes..."
                          className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        />
                        {searchLoading && (
                          <p className="text-xs text-gray-600 mt-1">Searching...</p>
                        )}

                        {/* Search Results Dropdown */}
                        {searchResults.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                            {searchResults.map((dish) => (
                              <div
                                key={dish._id}
                                className="p-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100"
                                onClick={() => handleSelectDish(dish)}
                              >
                                <span className="font-medium">{dish.name}</span>
                                <span className="text-sm text-gray-600 ml-2">
                                  ({dish.unit})
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Action Buttons */}
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={handleAddDish}
                          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-3 rounded focus:outline-none focus:shadow-outline text-sm"
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          onClick={handleCancelAddDish}
                          className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-3 rounded focus:outline-none focus:shadow-outline text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Current Dishes List */}
              <div>
                <h4 className="text-lg font-semibold text-gray-800 mb-4">Current Dishes</h4>
                {editedDishes.length > 0 ? (
                  <div className="space-y-3">
                    {editedDishes.map((item, index) => {
                      const dish = item.dish;
                      return (
                        <div key={index} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg bg-gray-50">
                          <div className="flex-1">
                            <h5 className="font-semibold text-gray-800">{dish.name}</h5>
                            <p className="text-sm text-gray-600">
                              Quantity: {dish.quantity} {dish.unit}
                            </p>
                          </div>
                          <button
                            onClick={() => handleRemoveDish(index)}
                            className="bg-red-500 hover:bg-red-700 text-white px-3 py-1 rounded text-sm ml-4"
                          >
                            Remove
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 italic">No dishes in this menu.</p>
                )}
              </div>

              {message && (
                <div className={`mt-4 p-3 rounded ${
                  message.includes('successfully') 
                    ? 'bg-green-100 text-green-600' 
                    : 'bg-red-100 text-red-500'
                }`}>
                  {message}
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
              <button
                onClick={handleCancelEdit}
                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveChanges}
                className="bg-lime-600 hover:bg-lime-700 text-white font-bold py-2 px-4 rounded"
                disabled={isLoading}
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MenuDetails;